/* eslint-disable */
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UiConstants } from '../../../util/core-utils';
import { Transition, UIRouter } from '@uirouter/angular';
import { ComponentStateResolver } from '../../../util/component-state/component-state-resolver';
import { BreadcrumbParent } from '../../../shared/breadcrumb/breadcrumb/breadcrumb.component';
import { NgForm, NgModel } from '@angular/forms';
import { LocalFieldValidationErrors, LocalFieldValidationErrorsFactory } from '../../../lib/util/services';
import { RightModel } from '../../../app.rights';
import { TranslateService } from '@ngx-translate/core';
import { RightResolver, RightService } from '../../../lib/right.service';
import { SmsTemplate, SmsTemplateService } from '../../../lib/sms/sms-template/sms-template.service';
import { StateName } from '../../../app.state-names';
import { FieldError, FieldErrors, ObservableErrorResourceParser } from '../../../lib/util/errors';
import { Dates, OffsetDateTime } from '../../../lib/util/dates';
import { List } from 'immutable';
import { Strings } from '../../../lib/util/strings';
import {EmptyMessage} from "../../../lib/util/messages";
/* eslint-enable */

@Component({
  selector: 'app-sms-template-base',
  templateUrl: './sms-template-base.component.html',
  styleUrls: ['./sms-template-base.component.scss']
})
export class SmsTemplateBaseComponent implements OnInit, AfterViewInit {

  // If you need to access certain classes from HTML, declare them here
  UiConstants = UiConstants;

  // Declare models before use
  editModel: SmsTemplateEditModel;
  detailModel: SmsTemplateDetailModel;

  // Component state resolver, determines the state of the component
  componentState: ComponentStateResolver;

  // Variables used for breadcrumb
  breadcrumbParents: BreadcrumbParent[] = [];
  breadcrumbSelf: string;
  compactSidebar: boolean = document.querySelector('body')!.classList.contains('sidebar-compact');

  // Form for validation
  @ViewChild('f', { static: true })
  fForm: NgForm;

  // Validated inputs
  @ViewChild('name')
  name: NgModel;

  @ViewChild('externalId')
  externalId: NgModel;

  @ViewChild('template')
  template: NgModel;

  // Field errors for server validation
  fieldErrors: SmsTemplateFieldErrorMap;

  private validatedInputs: LocalFieldValidationErrors<NgModel> =
    LocalFieldValidationErrorsFactory.empty();

  // Right model to store the rights available to the User
  rightModel: RightModel = RightModel.empty();

  constructor(private uiRouter: UIRouter,
              private transition: Transition,
              private smsTemplateService: SmsTemplateService,
              private translateService: TranslateService,
              private rightService: RightService) {
    this.fieldErrors = {};
    this.componentState = new ComponentStateResolver(uiRouter, transition,
      'id',
      {stateName: StateName.SMS_TEMPLATE_CREATE, stateHeaderKey: 'SMS_TEMPLATE_CREATE'},
      {stateName: StateName.SMS_TEMPLATE_EDIT, stateHeaderKey: 'SMS_TEMPLATE_EDIT'},
      {stateName: StateName.SMS_TEMPLATE_DETAIL, stateHeaderKey: 'SMS_TEMPLATE_DETAIL'});
  }

  ngOnInit() {
    this.initComponentState(); // Must be called first
    this.loadRightModels();
    this.initBreadcrumb();
  }

  ngAfterViewInit() {
    this.loadLocalFieldValidationErrors();
  }

  private initComponentState() {
    // Creates the editModel if not readonly
    if (!this.componentState.isDetailView()) {
      this.editModel = new SmsTemplateEditModel();
    }
    else {
      this.detailModel = new SmsTemplateDetailModel()
    }
    if (!this.componentState.isCreateView()) {
      this.loadModel();
    }
  }

  initBreadcrumb() {
    // Set breadcrumbSelf if createView, otherwise set in loadModel()
    if (this.componentState.isCreateView()) {
      this.translateService.get('SMS_TEMPLATE_CREATE').subscribe(
        (result: string) => {
          this.breadcrumbSelf = result;
        }
      );
    }
    this.translateService.get('MENU_NAVBAR_MENU_ADMINISTRATION').subscribe(
      (result: string) => {
        this.breadcrumbParents.push({name: result, uiSref: StateName.ADMIN_DASHBOARD});
      }
    );
    this.translateService.get('SMS_TEMPLATE_LIST').subscribe(
      (result: string) => {
        this.breadcrumbParents.push({name: result, uiSref: StateName.SMS_TEMPLATE_LIST});
      }
    );
  }

  // Get corresponding dictionary key
  getHeadingDictionaryKey(): string {
    return this.componentState.getCurrentHeaderKey();
  }

  hasLocalFieldError(field?: NgModel): boolean {
    return this.validatedInputs.hasLocalError(field);
  }

  removeFieldError(fieldError?: FieldError) {
    FieldErrors.remove(this.fieldErrors, fieldError);
  }

  private loadLocalFieldValidationErrors() {
    const validatedInputs = List.of(this.name,
      this.externalId,
      this.template
    );
    this.validatedInputs = LocalFieldValidationErrorsFactory.ofFormFields(this.fForm, validatedInputs);
  }

  // Loads user rights
  private loadRightModels() {
    this.rightService.getRightResolver().subscribe(
      (resolver: RightResolver) => {
        this.rightModel = RightModel.of(resolver);
      }
    );
  }

  // Called by the init method first
  private loadModel() {
    this.smsTemplateService.get({
      id: this.componentState.id!
    }).subscribe((smsTemplate: SmsTemplate.SmsTemplate) => {
      if (this.componentState.isEditView()) {
        this.loadEditModel(smsTemplate);
      }
      else if (this.componentState.isDetailView()) {
        this.loadDetailModel(smsTemplate);
      }
      this.breadcrumbSelf = smsTemplate.name;
    });
  }

  private loadEditModel(smsTemplate: SmsTemplate.SmsTemplate) {
    this.editModel = new SmsTemplateEditModel();
    this.editModel.id = smsTemplate.id;
    this.editModel.name = smsTemplate.name;
    this.editModel.externalId = smsTemplate.externalId;
    this.editModel.template = smsTemplate.template;
  }

  private loadDetailModel(smsTemplate: SmsTemplate.SmsTemplate) {
    this.detailModel = new SmsTemplateDetailModel();
    this.detailModel.id = smsTemplate.id;
    this.detailModel.name = smsTemplate.name;
    this.detailModel.externalId = smsTemplate.externalId;
    this.detailModel.template = smsTemplate.template;
    this.detailModel.creationTime = smsTemplate.creationTime;
    this.detailModel.updateTime = smsTemplate.updateTime;
  }

  submit() {
    if (this.componentState.isCreateView()) {
      this.create();
    }
    else if (this.componentState.isEditView()) {
      this.update();
    }
  }

  create() {
    if (this.hasLocalFieldError()) {
      return;
    }
    this.smsTemplateService.create({
      name: this.editModel.name,
      externalId: Strings.undefinedOrNonEmpty(this.editModel.externalId),
      template: this.editModel.template
    }).subscribe(
      (response: EmptyMessage) => {
        this.uiRouter.stateService.go(StateName.SMS_TEMPLATE_LIST);
      },
      (error: any) => {
        const res = ObservableErrorResourceParser.parseError(error);
        this.fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
      });
  }

  update() {
    if (this.hasLocalFieldError()) {
      return;
    }
    this.smsTemplateService.update({
      id: this.componentState.id!,
      name: this.editModel.name,
      externalId: this.editModel.externalId,
      template: this.editModel.template
    }).subscribe(
      (response: EmptyMessage) => {
        this.uiRouter.stateService.go(StateName.SMS_TEMPLATE_LIST);
      },
      (error: any) => {
        const res = ObservableErrorResourceParser.parseError(error);
        this.fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
      });
  }

  back() {
    window.history.back();
  }

}

// Used for create/edit views
class SmsTemplateEditModel {
  id: number;
  name: string = '';
  externalId: string = '';
  template: string = '';

}

// Used for detail view
class SmsTemplateDetailModel {
  id: number;
  name: string = '';
  externalId: string = '';
  template: string = '';
  creationTime: OffsetDateTime = Dates.emptyOffsetDateTime();
  updateTime: OffsetDateTime = Dates.emptyOffsetDateTime();
}

interface SmsTemplateFieldErrorMap {
  name?: FieldError;
  external_id?: FieldError;
}
